import { matchAppRoute } from '@gonfalon/navigator/matchers';

import { CANVAS_SEARCH_PARAM, decodeCanvasParam } from '../canvas/canvasState';
import type { CanvasState, CanvasView } from '../canvas/canvasState';

/**
 * A description of the page the user is looking at when they ask Vega a
 * question, derived entirely from the URL.
 *
 * The Vega panel is mounted at the observability root, outside each page's
 * `SearchContext` provider, so it cannot read filter state through React
 * context. The URL is the only shared surface — which is fine, because the
 * observability pages already round-trip their full query, time range and
 * selected resource through search params.
 */
export interface PageContext {
  /** Human-readable name of the view, e.g. "Traces" or "Error group". */
  view: string;
  /** The search/filter string the user has applied, if any. */
  query?: string;
  /** Human-readable time range, e.g. "last 6 hours". */
  timeRange?: string;
  /** Label/value pairs identifying the specific resource(s) in view. */
  details?: Array<[string, string]>;
}

/**
 * Opens the page-context block appended to the prompt. `unBuildPrompt` cuts the
 * message preview at this marker, so it must stay in sync with the string used
 * in `agentInsightsUtils`.
 */
export const PAGE_CONTEXT_MARKER = '\n\nCurrent view:';

/** Tabs the traces page understands; anything else is a stale or hand-edited URL. */
const TRACES_TABS = ['overview', 'spans', 'traces', 'conversations'];

/** The typed union of real app routes, as accepted by `matchAppRoute`. */
type AppRoutePattern = Parameters<typeof matchAppRoute>[0];

interface RouteDescriptor {
  pattern: AppRoutePattern;
  view: string;
  /** Identifiers to pull out of the matched path params. */
  details?: Array<[string, string]>;
}

const matchRoute = (pattern: AppRoutePattern, pathname: string) =>
  matchAppRoute(pattern, { pathname, search: '', hash: '' });

/**
 * Matched in order, first match wins — literal segments like `new` and `edit`
 * must precede the `:id` patterns they would otherwise be captured by.
 */
const ROUTES: RouteDescriptor[] = [
  // Artifacts live under /vega/ but are subjects in their own right, and the
  // artifact detail page mounts the Vega panel directly.
  {
    pattern: '/projects/:projectKey/vega/artifacts/:type/:externalId',
    view: 'Artifact',
    details: [
      ['Artifact type', 'type'],
      ['Artifact ID', 'externalId'],
    ],
  },
  { pattern: '/projects/:projectKey/vega/artifacts', view: 'Artifacts' },

  { pattern: '/projects/:projectKey/alerts/new', view: 'New alert' },
  { pattern: '/projects/:projectKey/alerts/:alertId/edit', view: 'Edit alert', details: [['Alert ID', 'alertId']] },
  { pattern: '/projects/:projectKey/alerts/:alertId', view: 'Alert', details: [['Alert ID', 'alertId']] },
  { pattern: '/projects/:projectKey/alerts', view: 'Alerts' },

  { pattern: '/projects/:projectKey/dashboards/new', view: 'New dashboard' },
  {
    pattern: '/projects/:projectKey/dashboards/:dashboardId/new',
    view: 'New graph',
    details: [['Dashboard ID', 'dashboardId']],
  },
  {
    pattern: '/projects/:projectKey/dashboards/:dashboardId/:graphId/edit',
    view: 'Edit graph',
    details: [
      ['Dashboard ID', 'dashboardId'],
      ['Graph ID', 'graphId'],
    ],
  },
  {
    pattern: '/projects/:projectKey/dashboards/:dashboardId/:graphId',
    view: 'Graph',
    details: [
      ['Dashboard ID', 'dashboardId'],
      ['Graph ID', 'graphId'],
    ],
  },
  {
    pattern: '/projects/:projectKey/dashboards/:dashboardId',
    view: 'Dashboard',
    details: [['Dashboard ID', 'dashboardId']],
  },
  { pattern: '/projects/:projectKey/dashboards', view: 'Dashboards' },

  {
    pattern: '/projects/:projectKey/errors/:issueId/instances/:instanceId',
    view: 'Error instance',
    details: [
      ['Error group ID', 'issueId'],
      ['Error instance ID', 'instanceId'],
    ],
  },
  {
    pattern: '/projects/:projectKey/errors/:issueId/instances',
    view: 'Error group instances',
    details: [['Error group ID', 'issueId']],
  },
  {
    pattern: '/projects/:projectKey/errors/:issueId',
    view: 'Error group',
    details: [['Error group ID', 'issueId']],
  },
  { pattern: '/projects/:projectKey/errors', view: 'Errors' },

  {
    pattern: '/projects/:projectKey/sessions/:sessionId',
    view: 'Session replay',
    details: [['Session ID', 'sessionId']],
  },
  { pattern: '/projects/:projectKey/sessions', view: 'Sessions' },

  { pattern: '/projects/:projectKey/logs', view: 'Logs' },
  { pattern: '/projects/:projectKey/traces', view: 'Traces' },
];

/** `service-map` -> `Service map`, `ignored` -> `Ignored`. */
const humanizeSegment = (segment: string) => {
  const spaced = segment.replace(/[-_]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** `last_6_hours` -> `last 6 hours`. */
const humanizePreset = (preset: string) => preset.replace(/[-_]+/g, ' ').trim();

const parseSearch = (search: string): URLSearchParams => {
  try {
    return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  } catch {
    // A hand-mangled query string (e.g. `?%%%`) must not take down the composer.
    return new URLSearchParams();
  }
};

const isValidDate = (value: string | null): value is string => {
  if (!value) {
    return false;
  }
  return !Number.isNaN(new Date(value).getTime());
};

const deriveTimeRange = (params: URLSearchParams): string | undefined => {
  // The picker shows the preset when there is one, so that is what the user
  // would describe if they were telling us the time range themselves.
  const relativeTime = params.get('relativeTime');
  if (relativeTime) {
    return humanizePreset(relativeTime);
  }

  const startDate = params.get('startDate');
  const endDate = params.get('endDate');
  if (isValidDate(startDate) && isValidDate(endDate)) {
    return `${startDate} to ${endDate}`;
  }

  return undefined;
};

/**
 * Search params that describe *what data is on screen* for a given view, beyond
 * the query and time range every page shares.
 */
const deriveSearchParamDetails = (view: string, params: URLSearchParams): Array<[string, string]> => {
  const details: Array<[string, string]> = [];

  if (view !== 'Traces' && view !== 'Trace') {
    return details;
  }

  const traceId = params.get('traceId');
  if (traceId) {
    details.push(['Trace ID', traceId]);
  }

  const spanId = params.get('spanId');
  if (spanId) {
    details.push(['Span ID', spanId]);
  }

  const tab = params.get('tab');
  if (tab && TRACES_TABS.includes(tab)) {
    details.push(['Tab', tab]);
  }

  const environments = params.get('environments');
  if (environments) {
    details.push(['Environments', environments.split(',').filter(Boolean).join(', ')]);
  }

  const services = params.get('services');
  if (services) {
    details.push(['Services', services.split(',').filter(Boolean).join(', ')]);
  }

  return details;
};

/**
 * Segments of a `/projects/:projectKey/...` path, or `undefined` when the path
 * is not project-scoped or names no page below the project.
 *
 * Parsed by hand rather than matched: the `/projects/:projectKey/*` splat is
 * not a declared app route, so it is not a pattern `matchAppRoute` accepts.
 */
const projectScopedSegments = (pathname: string): string[] | undefined => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'projects' || segments.length < 3) {
    return undefined;
  }
  return segments.slice(2);
};

/**
 * Vega's own chat surfaces (`/vega`, `/vega/history`, `/vega/settings`, and a
 * single conversation) — there the conversation is the subject, and describing
 * the page would only invite answers about the Vega UI itself.
 *
 * Artifacts are excluded: they sit under `/vega/` but are real subjects, and
 * the artifact detail page mounts the panel.
 */
const isVegaChatRoute = (pathname: string) => {
  const segments = projectScopedSegments(pathname);
  return segments?.[0] === 'vega' && segments[1] !== 'artifacts';
};

/**
 * Last-resort naming for a project-scoped page we have no specific knowledge
 * of, so Vega at least knows where the user is standing.
 */
const deriveFallbackView = (pathname: string): string | undefined => {
  const segments = projectScopedSegments(pathname);
  const last = segments?.[segments.length - 1];
  return last ? humanizeSegment(last) : undefined;
};

/**
 * View-specific implicit filters that the UI applies when the URL carries no
 * explicit `query` param.  The Errors page, for example, defaults to
 * `status=OPEN` — without this map Vega would treat the absence of a query as
 * "unfiltered" and could surface resolved or ignored groups.
 */
const VIEW_DEFAULT_QUERIES: Partial<Record<string, string>> = {
  Errors: 'status=OPEN',
};

const deriveDefaultQuery = (view: string): string | undefined => VIEW_DEFAULT_QUERIES[view];

/**
 * Translate the current location into something Vega can reason about. Returns
 * `undefined` when the page carries no useful subject (Vega's own pages, or
 * anything outside a project).
 */
export const derivePageContext = (pathname: string, search: string = ''): PageContext | undefined => {
  if (isVegaChatRoute(pathname)) {
    return undefined;
  }

  const params = parseSearch(search);

  const matched = ROUTES.map((route) => ({ route, match: matchRoute(route.pattern, pathname) })).find(
    ({ match }) => match !== null,
  );

  let view = matched?.route.view ?? deriveFallbackView(pathname);
  if (!view) {
    return undefined;
  }

  const details: Array<[string, string]> = [];
  if (matched?.route.details) {
    for (const [label, param] of matched.route.details) {
      const value = matched.match?.params[param];
      if (value) {
        details.push([label, value]);
      }
    }
  }

  // A selected trace turns the traces list into a single-trace view.
  if (view === 'Traces' && params.get('traceId')) {
    view = 'Trace';
  }

  details.push(...deriveSearchParamDetails(view, params));

  const query = params.get('query') || deriveDefaultQuery(view) || undefined;
  const timeRange = deriveTimeRange(params);

  return {
    view,
    ...(query ? { query } : {}),
    ...(timeRange ? { timeRange } : {}),
    ...(details.length > 0 ? { details } : {}),
  };
};

/**
 * Render the page context as a prompt block. Deliberately framed as a default
 * scope rather than a constraint: the user may well be asking about something
 * unrelated to whatever happens to be on screen.
 */
export const formatPageContextForPrompt = (context: PageContext): string => {
  const lines = [`${PAGE_CONTEXT_MARKER} the user is looking at ${context.view} in the LaunchDarkly UI.`];

  if (context.query) {
    lines.push(`- Filter: ${context.query}`);
  }
  if (context.timeRange) {
    lines.push(`- Time range: ${context.timeRange}`);
  }
  for (const [label, value] of context.details ?? []) {
    lines.push(`- ${label}: ${value}`);
  }

  // The user never sees this block and cannot dismiss it, so the model has to
  // say what it assumed — that admission is the only signal they get that the
  // answer was scoped to the page rather than to everything.
  lines.push(
    'Scope your answer to this view unless the question is about something else — treat it as where the user is standing, not a filter you must obey. When you do scope to it, say so in a few words so the user knows what you assumed.',
  );

  return lines.join('\n');
};

const O11Y_CANVAS_VIEWS: Record<string, string> = {
  session: 'Session replay',
  sessions: 'Sessions',
  trace: 'Trace',
  traces: 'Traces',
  error: 'Error',
  error_group: 'Error group',
  errors: 'Errors',
  logs: 'Logs',
  metric: 'Metric',
  conversation: 'Conversation',
  conversations: 'Conversations',
  service_details: 'Service',
};

const O11Y_CANVAS_DETAIL_LABELS: Record<string, string> = {
  id: 'ID',
  secureId: 'ID',
  spanID: 'Span ID',
  conversationId: 'Conversation ID',
  serviceName: 'Service',
  metricName: 'Metric',
  instanceId: 'Instance ID',
  traceId: 'Trace ID',
  timestamp: 'Timestamp',
};

const isVegaConversationPath = (pathname: string) => {
  const segments = projectScopedSegments(pathname);
  if (segments?.[0] !== 'vega' || !segments[1]) {
    return false;
  }
  return segments[1] !== 'artifacts' && segments[1] !== 'history' && segments[1] !== 'settings';
};

const pageContextFromCanvas = (canvas: CanvasState | null, defaultEnvironmentKey: string | undefined): PageContext => {
  const context: PageContext =
    !canvas || canvas.active.kind === 'empty'
      ? { view: 'Vega canvas', details: [['Canvas', 'empty']] }
      : pageContextFromCanvasView(canvas.active, defaultEnvironmentKey);
  const lastWrite = canvas?.lastWrite;
  if (!lastWrite) {
    return context;
  }
  return {
    ...context,
    details: [
      ...(context.details ?? []),
      [
        'Last Vega write',
        `${lastWrite.outcome} ${lastWrite.flagKey} in ${lastWrite.environmentKey}${
          lastWrite.flagVersion !== undefined ? ` (version ${lastWrite.flagVersion})` : ''
        }`,
      ],
    ],
  };
};

const pageContextFromCanvasView = (view: CanvasView, defaultEnvironmentKey: string | undefined): PageContext => {
  if (view.kind === 'empty') {
    return { view: 'Vega canvas', details: [['Canvas', 'empty']] };
  }
  if (view.kind === 'app-route') {
    const details: Array<[string, string]> = [['Route', view.pattern]];
    for (const [label, value] of Object.entries(view.params)) {
      details.push([label, value]);
    }
    const environmentKey = view.environmentKey ?? defaultEnvironmentKey;
    if (environmentKey) {
      details.push(['Environment', environmentKey]);
    }
    if (view.query) {
      details.push(['List query', view.query]);
    }
    return { view: 'Vega canvas', details };
  }
  const resource = view.resource;
  const details: Array<[string, string]> = [['Resource type', resource.type]];
  for (const [key, label] of Object.entries(O11Y_CANVAS_DETAIL_LABELS)) {
    const value = resource[key];
    if (typeof value === 'string' && value.length > 0) {
      details.push([label, value]);
    }
  }
  const query = typeof resource.query === 'string' && resource.query.length > 0 ? resource.query : undefined;
  return {
    view: O11Y_CANVAS_VIEWS[resource.type] ?? 'Vega canvas',
    ...(query ? { query } : {}),
    details,
  };
};

export const pageContextForPrompt = (
  pathname: string,
  search: string,
  workspaceEnabled: boolean,
): PageContext | undefined => {
  if (workspaceEnabled && isVegaConversationPath(pathname)) {
    const params = parseSearch(search);
    const canvas = decodeCanvasParam(params.get(CANVAS_SEARCH_PARAM));
    return pageContextFromCanvas(canvas, params.get('selected-env') ?? undefined);
  }
  return derivePageContext(pathname, search);
};
